import React, { useState, useEffect } from "react";
import {
  Box,
  Flex,
  Text,
  VStack,
  HStack,
  Button,
  useColorMode,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Tooltip,
  CircularProgress,
  CircularProgressLabel,
  Spinner,
} from "@chakra-ui/react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiActivity,
  FiShield,
  FiServer,
  FiSearch,
  FiDownload,
  FiRefreshCw,
  FiPrinter,
  FiList, // For Actions
  FiUser, // For User Logs
} from "react-icons/fi";
import { doc, onSnapshot, collection, getDocs, query, orderBy, limit, where, startAfter, Timestamp } from "firebase/firestore";
import { db } from "../../firebase/firebase";
import { useAuth } from "../../hooks/useAuth";
import { getThemeClasses } from "../../utils/themeUtils";
import { ChakraIcon } from "../../components/ChakraIcon";

const MotionBox = motion(Box);
const MotionTr = motion(Tr);

interface LogEntry {
  id: string;
  time: string;
  type: "AUTH" | "ACTIVITY" | "ERROR" | "SYSTEM" | "USER";
  user: string;
  email: string;
  action: string;
  details: string;
  status: "success" | "warning" | "error" | "info";
  timestamp?: any;
}

const mockLogs: LogEntry[] = [];

type TabType = "Authentication" | "Actions" | "Activity" | "User Logs" | "Tasks";

const SystemLogs: React.FC = () => {
  const { colorMode } = useColorMode();
  const isDark = colorMode === "dark";
  const theme = getThemeClasses(isDark);
  const [activeTab, setActiveTab] = useState<TabType>("Authentication");

  const { orgId } = useAuth();
  const [realLogs, setRealLogs] = useState<LogEntry[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [tasks, setTasks] = useState<any[]>([]);
  const [healthMetrics, setHealthMetrics] = useState<any>(null);
  const [isLoadingHealth, setIsLoadingHealth] = useState(false);

  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [displayCount, setDisplayCount] = useState(20);
  const [dateRangeType, setDateRangeType] = useState<"month" | "all" | "custom">("month");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [showAllActive, setShowAllActive] = useState(false);



  // Fetch users for the organization (moved out of the Tasks-only block)
  useEffect(() => {
    if (!orgId) return;
    const orgDoc = doc(db, "organization", orgId);
    const unsubscribe = onSnapshot(orgDoc, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setUsers(data.members || []);
      }
    });
    return () => unsubscribe();
  }, [orgId]);

  const fetchLogs = async () => {
    if (!orgId || users.length === 0) return;
    if (dateRangeType === "custom" && (!customStartDate || !customEndDate)) return;
    setIsInitialLoading(true);

    try {
      let category = "auth";
      if (activeTab === "Actions") category = "actions";
      else if (activeTab === "User Logs") category = "users";
      else if (activeTab === "Activity") category = "activity";
      else if (activeTab === "Tasks") {
        setRealLogs([]);
        setIsInitialLoading(false);
        return;
      }

      // Pattern: Fetch logs for each user from their own collection
      // path: organization/ID/SystemLogs/category/userId
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

      const logPromises = users.map(async (u) => {
        const logsRef = collection(db, "organization", orgId, "SystemLogs", category, u.id);
        
        let q;
        if (dateRangeType === "all") {
          q = query(logsRef, orderBy("timestamp", "desc"));
        } else if (dateRangeType === "custom") {
          const start = new Date(customStartDate);
          start.setHours(0, 0, 0, 0);
          const end = new Date(customEndDate);
          end.setHours(23, 59, 59, 999);
          q = query(
            logsRef,
            where("timestamp", ">=", start),
            where("timestamp", "<=", end),
            orderBy("timestamp", "desc")
          );
        } else {
          q = query(logsRef, where("timestamp", ">=", startOfMonth), orderBy("timestamp", "desc"));
        }
        
        const snap = await getDocs(q);
        return snap.docs.map(d => ({ id: d.id, ...(d.data() as any) }));
      });

      const results = await Promise.all(logPromises);
      const combinedLogs = results.flat() as LogEntry[];

      // Sort combined results by timestamp desc
      combinedLogs.sort((a: any, b: any) => {
        const tA = a.timestamp?.seconds || 0;
        const tB = b.timestamp?.seconds || 0;
        return tB - tA;
      });

      setRealLogs(combinedLogs);
      setDisplayCount(20);
      setHasMore(combinedLogs.length > 20);
    } catch (error) {
      console.error("Error fetching logs:", error);
    } finally {
      setIsInitialLoading(false);
    }
  };

  useEffect(() => {
    if (users.length > 0) {
      fetchLogs();
    }
  }, [orgId, activeTab, users.length, dateRangeType, customStartDate, customEndDate]);

  const handleLoadMore = () => {
    const nextCount = displayCount + 20;
    setDisplayCount(nextCount);
    setHasMore(realLogs.length > nextCount);
  };

  // Fetch users and tasks for System Health
  useEffect(() => {
    if (!orgId || activeTab !== "Tasks") return;

    const fetchHealthData = async () => {
      setIsLoadingHealth(true);
      try {
        // Fetch users
        // Users are now fetched globally for the component


        // Fetch all tasks from all projects in parallel
        const allTasks: any[] = [];

        // Get all projects for this organization
        const projectsCol = collection(db, `organization/${orgId}/projects`);
        const projectsSnap = await getDocs(projectsCol);

        // Fetch all channels in parallel for all projects
        const channelPromises = projectsSnap.docs.map(async (projectDoc) => {
          const projectId = projectDoc.id;
          const channelsCol = collection(db, `organization/${orgId}/projects/${projectId}/channels`);
          const channelsSnap = await getDocs(channelsCol);

          return {
            projectId,
            channels: channelsSnap.docs
          };
        });

        const projectChannels = await Promise.all(channelPromises);

        // Fetch all tasks in parallel for all channels
        const taskPromises = projectChannels.flatMap(({ projectId, channels }) =>
          channels.map(async (channelDoc) => {
            const channelId = channelDoc.id;
            const tasksCol = collection(db, `organization/${orgId}/projects/${projectId}/channels/${channelId}/tasks`);
            const tasksSnap = await getDocs(tasksCol);

            return tasksSnap.docs;
          })
        );

        const allTaskDocs = await Promise.all(taskPromises);

        // Flatten helper function
        const flattenTasksRecursive = (tasks: any[]): any[] => {
          const result: any[] = [];
          tasks.forEach(task => {
            result.push(task);
            if (task.subtasks && Array.isArray(task.subtasks) && task.subtasks.length > 0) {
              result.push(...flattenTasksRecursive(task.subtasks));
            }
          });
          return result;
        };

        // Process all task documents
        allTaskDocs.forEach(taskDocs => {
          taskDocs.forEach(taskDoc => {
            const taskData = taskDoc.data();
            // Tasks are stored in a 'data' array field
            if (taskData?.data && Array.isArray(taskData.data)) {
              const flatTasks = flattenTasksRecursive(taskData.data);
              allTasks.push(...flatTasks);
            }
          });
        });

        setTasks(allTasks);
        setIsLoadingHealth(false);
      } catch (error) {
        console.error("Error fetching health data:", error);
        setIsLoadingHealth(false);
      }
    };

    fetchHealthData();
  }, [orgId, activeTab]);

  // Calculate health metrics
  useEffect(() => {
    if (activeTab !== "Tasks" || users.length === 0) return;

    const calculateHealth = () => {
      const userMetrics = users.map(user => {
        // Find tasks assigned to this user
        // Tasks have assignedUsers array with user IDs or emails
        const assignedTasks = tasks.filter(task => {
          if (!task.assignedUsers || !Array.isArray(task.assignedUsers)) return false;
          return task.assignedUsers.some((assigned: any) =>
            assigned === user.id ||
            assigned === user.email ||
            assigned?.id === user.id ||
            assigned?.email === user.email
          );
        });

        const completedTasks = assignedTasks.filter(task =>
          task.status === 'Completed' || task.progress === 100
        );

        const assignedCount = assignedTasks.length;
        const completedCount = completedTasks.length;
        const completionRate = assignedCount > 0 ? (completedCount / assignedCount) * 100 : 0;

        let healthStatus = 'No Data';
        let healthColor = 'gray';
        if (assignedCount === 0) {
          healthStatus = 'No Data';
          healthColor = 'gray';
        } else if (completionRate < 40) {
          healthStatus = 'Critical';
          healthColor = 'red';
        } else if (completionRate < 60) {
          healthStatus = 'Needs Attention';
          healthColor = 'orange';
        } else if (completionRate < 80) {
          healthStatus = 'Good';
          healthColor = 'blue';
        } else {
          healthStatus = 'Excellent';
          healthColor = 'green';
        }

        return {
          user: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email,
          email: user.email,
          assignedTasks: assignedCount,
          completedTasks: completedCount,
          completionRate: Math.round(completionRate),
          healthStatus,
          healthColor,
        };
      });

      // Calculate overall health
      const totalAssigned = userMetrics.reduce((sum, m) => sum + m.assignedTasks, 0);
      const totalCompleted = userMetrics.reduce((sum, m) => sum + m.completedTasks, 0);
      const overallRate = totalAssigned > 0 ? (totalCompleted / totalAssigned) * 100 : 0;

      let overallStatus = 'No Data';
      let overallColor = 'gray';
      if (totalAssigned === 0) {
        overallStatus = 'No Data';
        overallColor = 'gray';
      } else if (overallRate < 40) {
        overallStatus = 'Critical';
        overallColor = 'red';
      } else if (overallRate < 60) {
        overallStatus = 'Needs Attention';
        overallColor = 'orange';
      } else if (overallRate < 80) {
        overallStatus = 'Good';
        overallColor = 'blue';
      } else {
        overallStatus = 'Excellent';
        overallColor = 'green';
      }

      setHealthMetrics({
        userMetrics,
        overall: {
          totalUsers: users.length,
          totalAssigned,
          totalCompleted,
          overallRate: Math.round(overallRate),
          overallStatus,
          overallColor,
        },
      });
    };

    calculateHealth();
  }, [users, tasks, activeTab]);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    setDateRangeType("month");
    setCustomStartDate("");
    setCustomEndDate("");
    setSearchTerm("");
    setShowAllActive(false);
  }, [activeTab]);

  useEffect(() => {
    setShowAllActive(false);
  }, [dateRangeType]);

  const tabs: { name: TabType; icon: any }[] = [
    { name: "Authentication", icon: FiShield },
    { name: "Actions", icon: FiList }, // Renamed from Activity
    { name: "Activity", icon: FiActivity }, // New empty tab
    { name: "User Logs", icon: FiUser },
    { name: "Tasks", icon: FiServer }, // Renamed from System Health
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case "success":
        return "green";
      case "error":
        return "red";
      case "warning":
        return "orange";
      default:
        return "blue";
    }
  };

  // Combine mock logs with real logs and apply local pagination (no local slicing for "all" mode or when "Fetch All Logs" button is clicked)
  const allLogs = (dateRangeType === "all" || showAllActive) ? [...realLogs, ...mockLogs] : [...realLogs, ...mockLogs].slice(0, displayCount);

  const filteredLogs = allLogs.filter((log) => {
    if (activeTab === "Authentication" && log.type !== "AUTH") return false;
    if (activeTab === "Actions" && log.type !== "ACTIVITY") return false;
    if (activeTab === "User Logs" && log.type !== "USER") return false;
    if (activeTab === "Activity" && log.type !== "ACTIVITY") return false;

    // Tasks (formerly System Health)
    if (activeTab === "Tasks" && log.type !== "SYSTEM" && log.type !== "ERROR") return false;

    return (
      log.user.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.action.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.type.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  // Export logs to CSV
  const handleExport = () => {
    if (filteredLogs.length === 0) return;

    const headers = ["Time", "Type", "User", "Email", "Action", "Details", "Status"];
    const csvContent = [
      headers.join(","),
      ...filteredLogs.map(log => [
        log.time,
        log.type,
        `"${log.user}"`,
        log.email,
        `"${log.action}"`,
        `"${log.details.replace(/"/g, '""')}"`,
        log.status
      ].join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `system-logs-${activeTab.toLowerCase().replace(/ /g, '-')}-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  // Print logs
  const handlePrint = () => {
    if (filteredLogs.length === 0) return;

    const printWindow = window.open('', '', 'height=600,width=800');
    if (!printWindow) return;

    printWindow.document.write('<html><head><title>System Logs - ' + activeTab + '</title>');
    printWindow.document.write('<style>');
    printWindow.document.write('body { font-family: Arial, sans-serif; margin: 20px; }');
    printWindow.document.write('h1 { color: #333; }');
    printWindow.document.write('table { width: 100%; border-collapse: collapse; margin-top: 20px; }');
    printWindow.document.write('th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }');
    printWindow.document.write('th { background-color: #f2f2f2; font-weight: bold; }');
    printWindow.document.write('tr:nth-child(even) { background-color: #f9f9f9; }');
    printWindow.document.write('@media print { button { display: none; } }');
    printWindow.document.write('</style></head><body>');
    printWindow.document.write('<h1>System Logs - ' + activeTab + '</h1>');
    printWindow.document.write('<p>Generated on: ' + new Date().toLocaleString() + '</p>');
    printWindow.document.write('<table>');
    printWindow.document.write('<thead><tr><th>Time</th><th>Type</th><th>User</th><th>Email</th><th>Action</th><th>Details</th><th>Status</th></tr></thead>');
    printWindow.document.write('<tbody>');

    filteredLogs.forEach(log => {
      printWindow.document.write('<tr>');
      printWindow.document.write('<td>' + log.time + '</td>');
      printWindow.document.write('<td>' + log.type + '</td>');
      printWindow.document.write('<td>' + log.user + '</td>');
      printWindow.document.write('<td>' + log.email + '</td>');
      printWindow.document.write('<td>' + log.action + '</td>');
      printWindow.document.write('<td>' + log.details + '</td>');
      printWindow.document.write('<td>' + log.status + '</td>');
      printWindow.document.write('</tr>');
    });

    printWindow.document.write('</tbody></table>');
    printWindow.document.write('</body></html>');
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  const getActionConfig = (log: LogEntry) => {
    if (activeTab === "Activity") {
      if (log.action.startsWith("Visited ")) {
        const module = log.action.replace("Visited ", "").replace(" module", "");
        let color = "gray";
        switch (module) {
          case "Users": color = "blue"; break;
          case "Projects": color = "purple"; break;
          case "Tasks": color = "orange"; break;
          case "TimeSheets": color = "green"; break;
          case "Progress": color = "pink"; break;
          case "KPI-Metric": color = "cyan"; break;
          case "Calendar": color = "teal"; break;
          case "Departments": color = "yellow"; break;
          case "WorkGroup": color = "teal"; break;
          case "Overview": color = "blue"; break;
          case "SystemLogs": color = "gray"; break;
          default: color = "blue"; // Default for other modules
        }
        return { text: module, color };
      }
      if (log.action.includes("Viewed tasks inside project")) {
        return { text: "Tasks", color: "orange" };
      }
    }

    // Default/Legacy logic for other tabs
    let color = "gray";
    if (log.action === 'Login' || log.action === 'Sign Out') color = 'blue';
    else if (log.action === 'Task Management') color = 'purple';
    else if (log.action === 'Added Project' || log.action === 'Updated Project' || log.action === 'Deleted Project') color = 'green';
    else if (log.action === 'Added User') color = 'green';
    else if (log.action === 'Deleted User') color = 'red';
    else if (log.action === 'Updated User' || log.action === 'Updated Assignments') color = 'orange';
    else if (log.action === 'KPI Management') color = 'cyan';
    else if (log.action === 'Department') color = 'pink';
    else if (log.action === 'Work Group') color = 'teal';
    else if (log.action === 'Leave Calendar') color = 'yellow';

    return { text: log.action, color };
  };

  return (
    <Flex direction="column" maxH="calc(100vh - 40px)" w="full" p={2} gap={4} overflow="hidden">
      {/* Header Section */}
      <div className={`flex justify-between items-center p-4 rounded-xl border ${isDark ? 'relative overflow-hidden bg-gradient-to-br from-[#0B0B0F] via-[#1A1C1F] to-[#0B0B0F] border-[#2E2F33] shadow-lg' : 'bg-gradient-to-r from-[#FFF7E6] via-[#FAEDD0] to-[#FFF8EA] border-gray-200 shadow-sm'} flex-shrink-0`}>
        {isDark && (
          <>
            <span className="pointer-events-none absolute inset-0 [background:radial-gradient(120%_60%_at_50%_120%,rgba(181,145,67,0.12)_0%,rgba(0,0,0,0)_60%)]" />
            <span className="pointer-events-none absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#FBD282]/25 to-transparent" />
          </>
        )}
        <div className="flex flex-col space-y-1">
          <h1 className={`text-2xl font-extrabold tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>System Details</h1>
          <p className={`text-xs ${isDark ? 'text-[#C3C9D3]' : 'text-gray-600'}`}>
            System Logs & Activity Monitoring
          </p>
        </div>
      </div>

      {/* Custom Tabs */}
      <Flex gap={2} overflowX="auto" pb={2} flexShrink={0} className=" p-1">
        {tabs.map((tab) => {
          const isActive = activeTab === tab.name;
          return (
            <button
              key={tab.name}
              onClick={() => setActiveTab(tab.name)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${isActive
                ? isDark
                  ? "bg-[#252830] text-[#FBD282] border-[#B59143] shadow-[0_0_12px_rgba(251,210,130,0.15)]"
                  : "bg-orange-50 text-orange-600 border-orange-200 shadow-sm"
                : isDark
                  ? "bg-transparent text-gray-400 border-transparent hover:text-white hover:bg-white/5"
                  : "bg-transparent text-gray-500 border-transparent hover:text-gray-900 hover:bg-gray-100"
                }`}
            >
              <ChakraIcon as={tab.icon} className={`w-4 h-4 ${isActive ? (isDark ? 'text-[#FBD282]' : 'text-orange-500') : 'text-gray-400'}`} />
              <span>{tab.name}</span>
            </button>
          );
        })}
      </Flex>

      {/* Filter and Content Area */}
      <MotionBox
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className={`flex-1 flex flex-col min-h-0 rounded-xl border p-4 ${isDark
          ? "bg-[#1A1C1F] border-[#2E2F33]"
          : "bg-white border-slate-200"
          }`}
      >
        {/* Toolbar */}
        <div className="flex flex-col md:flex-row justify-between items-stretch md:items-center gap-4 mb-6 flex-shrink-0">
          <div className="relative flex-1 max-w-md">
            <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <ChakraIcon as={FiSearch} className="text-gray-400 w-4 h-4" />
            </span>
            <input
              type="text"
              placeholder="Filter logs by user, action or keyword..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full pl-10 pr-4 py-2 text-sm rounded-lg border outline-none transition-all ${isDark
                ? "bg-[#14161B] border-[#2E2F33] text-white placeholder-gray-500 focus:border-[#B59143] focus:ring-1 focus:ring-[#B59143]"
                : "bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400 focus:border-orange-400 focus:ring-1 focus:ring-orange-400"
                }`}
            />
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <select
              value={dateRangeType}
              onChange={(e) => setDateRangeType(e.target.value as any)}
              className={`text-xs px-3 py-2 rounded-lg border outline-none transition-all ${isDark
                  ? "bg-[#14161B] border-[#2E2F33] text-white"
                  : "bg-gray-50 border-gray-200 text-gray-900"
                }`}
            >
              <option value="month">This Month</option>
              <option value="all">All Time</option>
              <option value="custom">Custom Range</option>
            </select>

            {dateRangeType === "custom" && (
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className={`text-xs px-3 py-1.5 rounded-lg border outline-none transition-all ${isDark
                      ? "bg-[#14161B] border-[#2E2F33] text-white"
                      : "bg-gray-50 border-gray-200 text-gray-900"
                    }`}
                />
                <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>to</span>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className={`text-xs px-3 py-1.5 rounded-lg border outline-none transition-all ${isDark
                      ? "bg-[#14161B] border-[#2E2F33] text-white"
                      : "bg-gray-50 border-gray-200 text-gray-900"
                    }`}
                />
              </div>
            )}

            <span className={`text-xs px-3 py-1.5 rounded-lg border font-medium ${isDark
              ? "bg-[#252830]/30 border-[#B59143]/20 text-[#FBD282]"
              : "bg-orange-50 border-orange-100 text-orange-600"
              }`}>
              {activeTab === "Tasks"
                ? `${healthMetrics?.overall?.totalAssigned || 0} Tasks`
                : `${filteredLogs.length} Records`
              }
            </span>

            {activeTab !== "Tasks" && (
              <>
                {dateRangeType !== "all" && (
                  <button
                    onClick={() => setShowAllActive(prev => !prev)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-semibold transition-all ${
                      showAllActive
                        ? isDark
                          ? "border-[#B59143] bg-[#252830] text-[#FBD282] shadow-[0_0_12px_rgba(251,210,130,0.15)]"
                          : "border-orange-200 bg-orange-50 text-orange-600 shadow-sm"
                        : isDark
                          ? "border-[#2E2F33] bg-[#14161B] text-gray-300 hover:text-[#FBD282] hover:border-[#B59143]"
                          : "border-gray-200 bg-white text-gray-700 hover:text-orange-600 hover:border-orange-300"
                    }`}
                  >
                    <ChakraIcon as={FiRefreshCw} className="w-3.5 h-3.5" />
                    <span>{showAllActive ? "Reset View" : "Fetch All Logs"}</span>
                  </button>
                )}

                {!showAllActive && dateRangeType !== "all" && hasMore && (
                  <button
                    onClick={handleLoadMore}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-semibold transition-all ${isDark
                      ? "border-[#2E2F33] bg-[#14161B] text-gray-300 hover:text-[#FBD282] hover:border-[#B59143]"
                      : "border-gray-200 bg-white text-gray-700 hover:text-orange-600 hover:border-orange-300"
                      }`}
                  >
                    <ChakraIcon as={FiRefreshCw} className="w-3.5 h-3.5" />
                    <span>Load More</span>
                  </button>
                )}
                <button
                  onClick={handleExport}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-semibold transition-all ${isDark
                    ? "border-[#2E2F33] bg-[#14161B] text-gray-300 hover:text-[#FBD282] hover:border-[#B59143]"
                    : "border-gray-200 bg-white text-gray-700 hover:text-orange-600 hover:border-orange-300"
                    }`}
                >
                  <ChakraIcon as={FiDownload} className="w-3.5 h-3.5" />
                  <span>Export</span>
                </button>
                <button
                  onClick={handlePrint}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-semibold transition-all ${isDark
                    ? "border-[#2E2F33] bg-[#14161B] text-gray-300 hover:text-[#FBD282] hover:border-[#B59143]"
                    : "border-gray-200 bg-white text-gray-700 hover:text-orange-600 hover:border-orange-300"
                    }`}
                >
                  <ChakraIcon as={FiPrinter} className="w-3.5 h-3.5" />
                  <span>Print</span>
                </button>
              </>
            )}
          </div>
        </div>

        {/* Conditional rendering based on active tab */}
        {activeTab === "Tasks" ? (
          // System Health View
          isLoadingHealth ? (
            <Flex direction="column" align="center" justify="center" py={20}>
              <Spinner
                size="xl"
                thickness="4px"
                speed="0.65s"
                color={isDark ? "#FBD282" : "orange.500"}
                mb={4}
              />
              <Text color={isDark ? "gray.400" : "gray.600"} fontSize="lg">
                Loading health data...
              </Text>
              <Text color={isDark ? "gray.500" : "gray.500"} fontSize="sm" mt={2}>
                Fetching tasks from all projects
              </Text>
            </Flex>
          ) : healthMetrics ? (
            <div className="flex-1 overflow-y-auto pr-1 space-y-6 min-h-0">
              {/* Overall Health Card */}
              <div className={`p-6 rounded-xl border transition-all hover:shadow-md ${isDark
                ? "bg-[#14161B]/80 border-[#2E2F33] text-white shadow-inner"
                : "bg-white border-gray-200 text-gray-900"
                }`}>
                <Flex justify="space-between" align="center" mb={4}>
                  <Text fontSize="lg" fontWeight="bold">
                    Overall Task Completion
                  </Text>
                  <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${healthMetrics.overall.overallColor === "green" ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30" :
                    healthMetrics.overall.overallColor === "blue" ? "bg-blue-500/20 text-blue-400 border border-blue-500/30" :
                      healthMetrics.overall.overallColor === "orange" ? "bg-amber-500/20 text-amber-400 border border-amber-500/30" :
                        healthMetrics.overall.overallColor === "red" ? "bg-rose-500/20 text-rose-400 border border-rose-500/30" :
                          "bg-gray-500/20 text-gray-400 border border-gray-500/30"
                    }`}>
                    {healthMetrics.overall.overallStatus}
                  </span>
                </Flex>

                <Flex gap={10} align="center" wrap="wrap">
                  {/* Donut Chart */}
                  <VStack spacing={0} align="center">
                    <CircularProgress
                      value={healthMetrics.overall.overallRate}
                      size="120px"
                      thickness="12px"
                      color={
                        healthMetrics.overall.overallColor === "green" ? "green.500" :
                          healthMetrics.overall.overallColor === "blue" ? "blue.500" :
                            healthMetrics.overall.overallColor === "orange" ? "orange.500" :
                              "red.500"
                      }
                      trackColor={isDark ? "gray.700" : "gray.200"}
                    >
                      <CircularProgressLabel>
                        <VStack spacing={0}>
                          <Text fontSize="2xl" fontWeight="bold" color={isDark ? "white" : "gray.900"}>
                            {healthMetrics.overall.overallRate}%
                          </Text>
                          <Text fontSize="2xs" color={isDark ? "gray.400" : "gray.600"} textTransform="uppercase" letterSpacing="wide">
                            Complete
                          </Text>
                        </VStack>
                      </CircularProgressLabel>
                    </CircularProgress>
                  </VStack>

                  {/* Stats Card Grid */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 flex-1 justify-around">
                    <div className={`flex flex-col items-center justify-center p-3 rounded-lg border ${isDark ? 'bg-black/30 border-white/5' : 'bg-gray-50 border-gray-100'}`}>
                      <span className={`text-xs uppercase tracking-wider font-semibold ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Total Users</span>
                      <span className="text-3xl font-extrabold">{healthMetrics.overall.totalUsers}</span>
                    </div>
                    <div className={`flex flex-col items-center justify-center p-3 rounded-lg border ${isDark ? 'bg-black/30 border-white/5' : 'bg-gray-50 border-gray-100'}`}>
                      <span className={`text-xs uppercase tracking-wider font-semibold ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Total Tasks</span>
                      <span className="text-3xl font-extrabold">{healthMetrics.overall.totalAssigned}</span>
                    </div>
                    <div className={`flex flex-col items-center justify-center p-3 rounded-lg border ${isDark ? 'bg-black/30 border-white/5' : 'bg-gray-50 border-gray-100'}`}>
                      <span className={`text-xs uppercase tracking-wider font-semibold ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Completed</span>
                      <span className="text-3xl font-extrabold text-emerald-500">{healthMetrics.overall.totalCompleted}</span>
                    </div>
                    <div className={`flex flex-col items-center justify-center p-3 rounded-lg border ${isDark ? 'bg-black/30 border-white/5' : 'bg-gray-50 border-gray-100'}`}>
                      <span className={`text-xs uppercase tracking-wider font-semibold ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Pending</span>
                      <span className="text-3xl font-extrabold text-amber-500">{healthMetrics.overall.totalAssigned - healthMetrics.overall.totalCompleted}</span>
                    </div>
                  </div>
                </Flex>
              </div>

              {/* User Health Table */}
              <TableContainer>
                <Table variant="simple" size="sm">
                  <Thead>
                    <Tr>
                      <Th color={isDark ? "gray.400" : "gray.500"} fontSize="xs" textTransform="uppercase" letterSpacing="wider">Identity</Th>
                      <Th color={isDark ? "gray.400" : "gray.500"} fontSize="xs" textTransform="uppercase" letterSpacing="wider" textAlign="center">Assigned</Th>
                      <Th color={isDark ? "gray.400" : "gray.500"} fontSize="xs" textTransform="uppercase" letterSpacing="wider" textAlign="center">Completed</Th>
                      <Th color={isDark ? "gray.400" : "gray.500"} fontSize="xs" textTransform="uppercase" letterSpacing="wider" textAlign="center">Completion %</Th>
                      <Th color={isDark ? "gray.400" : "gray.500"} fontSize="xs" textTransform="uppercase" letterSpacing="wider" textAlign="center">Health Status</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {healthMetrics.userMetrics.map((metric: any, index: number) => (
                      <Tr key={index} _hover={{ bg: isDark ? "whiteAlpha.50" : "gray.50" }}>
                        <Td>
                          <div className="flex flex-col">
                            <span className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{metric.user}</span>
                            <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{metric.email}</span>
                          </div>
                        </Td>
                        <Td textAlign="center">
                          <Text fontSize="sm" color={isDark ? "white" : "gray.800"}>
                            {metric.assignedTasks}
                          </Text>
                        </Td>
                        <Td textAlign="center">
                          <Text fontSize="sm" color="green.500" fontWeight="medium">
                            {metric.completedTasks}
                          </Text>
                        </Td>
                        <Td textAlign="center">
                          <Text fontSize="sm" fontWeight="bold" color={
                            metric.healthColor === "green" ? "green.500" :
                              metric.healthColor === "blue" ? "blue.500" :
                                metric.healthColor === "orange" ? "orange.500" :
                                  "red.500"
                          }>
                            {metric.completionRate}%
                          </Text>
                        </Td>
                        <Td textAlign="center">
                          <span className={`text-xs font-semibold ${
                            metric.healthColor === "green" ? "text-emerald-500" :
                            metric.healthColor === "blue" ? "text-blue-500" :
                            metric.healthColor === "orange" ? "text-amber-500" :
                            metric.healthColor === "red" ? "text-rose-500" :
                            isDark ? "text-gray-300" : "text-gray-700"
                          }`}>
                            {metric.healthStatus}
                          </span>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>

              {healthMetrics.userMetrics.length === 0 && (
                <Flex direction="column" align="center" justify="center" py={10}>
                  <ChakraIcon as={FiServer} boxSize={10} color="gray.500" mb={4} />
                  <Text color="gray.500">No user data available</Text>
                </Flex>
              )}
            </div>
          ) : (
            <Flex direction="column" align="center" justify="center" py={10}>
              <ChakraIcon as={FiServer} boxSize={10} color="gray.500" mb={4} />
              <Text color="gray.500">Loading health data...</Text>
            </Flex>
          )
        ) : (
          // Original Logs View
          <>
            <TableContainer overflowX="auto" overflowY="auto" flex="1" minH="0">
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th color={isDark ? "gray.400" : "gray.500"} fontSize="xs" textTransform="uppercase" letterSpacing="wider" width="12%" minW="130px">Time</Th>
                    <Th color={isDark ? "gray.400" : "gray.500"} fontSize="xs" textTransform="uppercase" letterSpacing="wider" width="23%" minW="200px">Identity</Th>
                    <Th color={isDark ? "gray.400" : "gray.500"} fontSize="xs" textTransform="uppercase" letterSpacing="wider" width="20%" minW="160px">Action</Th>
                    <Th color={isDark ? "gray.400" : "gray.500"} fontSize="xs" textTransform="uppercase" letterSpacing="wider" width="45%" minW="300px">Details</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {filteredLogs.map((log) => {
                    const { text: actionText, color: actionColor } = getActionConfig(log);
                    return (
                      <Tr
                        key={log.id}
                        _hover={{ bg: isDark ? "whiteAlpha.50" : "gray.50" }}
                      >
                        <Td color={isDark ? "gray.300" : "gray.700"}>
                          <span className="text-xs text-gray-400">
                            {log.time}
                          </span>
                        </Td>
                        <Td>
                          <div className="flex flex-col">
                            <span className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{log.user}</span>
                            <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{log.email}</span>
                          </div>
                        </Td>
                        <Td>
                          <span className={`text-sm font-medium ${actionColor === "green" ? "text-emerald-500" :
                            actionColor === "red" ? "text-rose-500" :
                              actionColor === "orange" ? "text-amber-500" :
                                actionColor === "blue" ? "text-blue-500" :
                                  actionColor === "purple" ? "text-purple-500" :
                                    actionColor === "cyan" ? "text-cyan-500" :
                                      actionColor === "pink" ? "text-pink-500" :
                                        actionColor === "teal" ? "text-teal-500" :
                                          actionColor === "yellow" ? "text-yellow-600" :
                                            isDark ? "text-gray-300" : "text-gray-700"
                            }`}>
                            {actionText}
                          </span>
                        </Td>
                        <Td>
                          <span className={`text-xs px-2.5 py-1.5 rounded border block truncate max-w-[500px] ${isDark
                            ? "bg-black/30 border-[#2E2F33] text-gray-300"
                            : "bg-gray-50 border-gray-200 text-gray-700"
                            }`} title={log.details}>
                            {log.details}
                          </span>
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </TableContainer>


            {filteredLogs.length === 0 && !isInitialLoading && (
              <Flex direction="column" align="center" justify="center" py={10}>
                <ChakraIcon as={FiSearch} boxSize={10} color="gray.500" mb={4} />
                <Text color="gray.500">No logs found matching your criteria</Text>
              </Flex>
            )}

            {isInitialLoading && (
              <Flex direction="column" align="center" justify="center" py={10}>
                <Spinner size="lg" color="blue.500" mb={4} />
                <Text color="gray.500">Fetching logs...</Text>
              </Flex>
            )}
          </>
        )}
      </MotionBox>
    </Flex >
  );
};

export default SystemLogs;
